import React from "react";

// Texts of the missing reviews calculator. The English version is written for
// US search (target keyword: "google review calculator"), not translated word
// for word.
const content = {
  fr: {
    seo: {
      title:
        "Calculateur d'avis manquants Google | Combien d'avis 5 étoiles pour atteindre votre objectif ?",
      description:
        "Calculez combien d'avis 5 étoiles il vous faut pour atteindre votre note cible sur Google. Outil gratuit pour commerces, franchises et agences.",
      ogTitle: "Calculateur d'avis manquants Google | Localranker",
      ogDescription:
        "Calculez gratuitement combien d'avis 5 étoiles il vous faut pour atteindre votre note cible sur Google.",
    },
    hero: {
      title: "Calculateur d'avis manquants",
      subtitle:
        "Combien d'avis 5 étoiles vous faut-il pour atteindre votre objectif de note Google ?",
      cta: "Tester maintenant",
    },
    form: {
      reviewCount: "Nombre d'avis actuels",
      reviewCountPlaceholder: "ex: 47",
      currentRating: "Note moyenne actuelle",
      currentRatingPlaceholder: "ex: 3.8",
      targetRating: "Note cible souhaitée",
      targetRatingPlaceholder: "ex: 4.5",
    },
    result: {
      alreadyMetTitle: "Objectif déjà atteint !",
      alreadyMetDesc: "Votre note actuelle est déjà égale ou supérieure à votre cible.",
      unreachableTitle: "Objectif impossible",
      unreachableDesc:
        "Une note cible de 5.0 ne peut être atteinte qu'avec uniquement des avis 5 étoiles.",
      neededTitle: "avis 5 étoiles nécessaires",
      neededDesc: (current, newRating, newTotal) => (
        <>
          pour passer de <strong>{current}</strong> à{" "}
          <strong>{newRating}</strong> avec{" "}
          <strong>{newTotal}</strong> avis au total
        </>
      ),
      currentRating: "Note actuelle",
      projectedRating: "Note projetée",
      currentReviews: "Avis actuels",
      reviewsToGet: "Avis à obtenir",
    },
    why: {
      title: "Pourquoi les avis Google sont essentiels pour votre entreprise locale ?",
      text: "Les avis Google sont devenus l'un des critères de choix les plus importants pour les consommateurs. Votre note moyenne influence directement votre taux de clics dans les résultats Google, votre positionnement dans le Local Pack et, in fine, votre chiffre d'affaires.",
      stats: [
        {
          number: "87%",
          text: "des consommateurs lisent les avis en ligne avant un achat local",
        },
        {
          number: "+35%",
          text: "de clics pour les fiches notées 4.5+ vs celles sous 4.0",
        },
        {
          number: "4.5",
          text: "note idéale à viser — le sweet spot crédibilité + SEO",
        },
      ],
    },
    how: {
      title: "Comment fonctionne le calculateur d'avis manquants ?",
      intro:
        "Notre outil utilise une formule mathématique simple pour déterminer le nombre exact d'avis 5 étoiles nécessaires pour atteindre votre note cible. Trois étapes suffisent :",
      steps: [
        {
          title: "Nombre d'avis actuels",
          text: "Le total affiché sur votre fiche Google Business Profile.",
        },
        {
          title: "Note moyenne actuelle",
          text: "Votre note sur 5 telle qu'affichée par Google.",
        },
        {
          title: "Note cible souhaitée",
          text: "La note que vous souhaitez atteindre (par ex. 4.5).",
        },
      ],
      outro:
        "Le calculateur projette ensuite combien de nouveaux avis 5 étoiles il vous faudra obtenir, en tenant compte du poids de vos avis existants. Indispensable pour piloter votre stratégie d'e-reputation.",
    },
    examples: {
      title: "Combien d'avis faut-il pour passer de 4.0 à 4.5 sur Google ?",
      intro:
        "La réponse dépend de votre volume d'avis existant. Plus vous avez d'avis, plus il en faut pour faire bouger la moyenne.",
      items: [
        { header: "10 avis existants", big: "~10" },
        { header: "50 avis existants", big: "~50" },
        { header: "200 avis existants", big: "~200" },
      ],
      itemText: "avis 5 étoiles pour passer de 4.0 à 4.5",
      outro:
        "C'est pourquoi il est crucial de commencer à collecter des avis le plus tôt possible : plus votre base est petite, plus chaque nouvel avis positif a un impact fort sur votre note globale.",
    },
    ideal: {
      title: "Quelle est la note Google idéale à viser ?",
      scale: {
        bad: "Insuffisant",
        ok: "Correct",
        good: "Idéal",
        sus: "Suspect",
      },
      text: (
        <>
          Une note en dessous de 4.0 décourage une grande partie des prospects.
          À l'inverse, une note parfaite de 5.0 peut paraître suspecte.
          Google utilise la note moyenne comme signal de classement dans le Local Pack — les fiches
          au-dessus de 4.0 sont systématiquement favorisées. Visez <strong>4.5 comme objectif réaliste</strong>.
        </>
      ),
    },
    tips: {
      title: "Comment obtenir plus d'avis 5 étoiles sur Google ?",
      intro:
        "Obtenir des avis positifs ne relève pas du hasard. Voici les bonnes pratiques des entreprises locales les plus performantes :",
      items: [
        {
          icon: "📩",
          title: "Demandez systématiquement",
          text: "Après chaque interaction client réussie, envoyez un SMS ou un email avec un lien direct vers votre fiche Google.",
        },
        {
          icon: "🔗",
          title: "Facilitez le parcours",
          text: "Utilisez un lien court ou un QR code qui mène directement au formulaire d'avis Google.",
        },
        {
          icon: "💬",
          title: "Répondez à tous les avis",
          text: "Les avis positifs comme négatifs. Les clients qui voient des réponses sont plus enclins à laisser le leur.",
        },
        {
          icon: "👥",
          title: "Formez vos équipes",
          text: "Chaque collaborateur en contact client doit savoir quand et comment demander un avis.",
        },
        {
          icon: "⚡",
          title: "Automatisez la collecte",
          text: "Des outils comme LocalRanker permettent d'envoyer des demandes d'avis automatiques après chaque visite ou achat.",
        },
      ],
    },
    seoImpact: {
      title: "Les avis Google impactent-ils le référencement local (SEO local) ?",
      intro:
        "Oui, de manière significative. Google a confirmé que les avis sont l'un des principaux facteurs de classement dans les résultats locaux. Trois signaux sont pris en compte :",
      factors: [
        {
          icon: "📈",
          title: "Volume",
          text: "Plus vous avez d'avis, plus Google considère votre fiche comme légitime et populaire.",
        },
        {
          icon: "⭐",
          title: "Note moyenne",
          text: "Une note élevée est un signal de qualité qui améliore votre positionnement.",
        },
        {
          icon: "🕐",
          title: "Fraîcheur",
          text: "Des avis récents et réguliers montrent que votre entreprise est active.",
        },
      ],
      outro:
        "En travaillant activement sur votre collecte d'avis, vous améliorez à la fois votre image de marque et votre visibilité dans Google Maps et le Local Pack.",
    },
  },

  en: {
    seo: {
      title: "Google Review Calculator (Free) | Localranker",
      description:
        "Free Google review calculator: enter your review count and star rating to see how many 5-star reviews you need to reach your target Google rating.",
    },
    hero: {
      title: "Google Review Calculator",
      subtitle:
        "How many 5-star reviews do you need to reach your target Google rating? Find out in seconds.",
      cta: "Try it now",
    },
    form: {
      reviewCount: "Current number of reviews",
      reviewCountPlaceholder: "e.g. 47",
      currentRating: "Current average rating",
      currentRatingPlaceholder: "e.g. 3.8",
      targetRating: "Target rating",
      targetRatingPlaceholder: "e.g. 4.5",
    },
    result: {
      alreadyMetTitle: "Target already reached!",
      alreadyMetDesc: "Your current rating is already at or above your target.",
      unreachableTitle: "Target out of reach",
      unreachableDesc:
        "A 5.0 rating is only possible if every single review is 5 stars.",
      neededTitle: "5-star reviews needed",
      neededDesc: (current, newRating, newTotal) => (
        <>
          to go from <strong>{current}</strong> to{" "}
          <strong>{newRating}</strong> with{" "}
          <strong>{newTotal}</strong> reviews in total
        </>
      ),
      currentRating: "Current rating",
      projectedRating: "Projected rating",
      currentReviews: "Current reviews",
      reviewsToGet: "Reviews to get",
    },
    why: {
      title: "Why does your Google rating matter for a local business?",
      text: "Your star rating is one of the first things people see when they find your business on Google Search or Google Maps. It shapes whether they click on your listing, call you or visit, and it is part of how Google decides which businesses to show in the local pack.",
      stats: [
        {
          number: "1–5",
          text: "Google shows your average on a 5-star scale, rounded to one decimal",
        },
        {
          number: "5★",
          text: "every new 5-star review pulls your average up, faster when you have few reviews",
        },
        {
          number: "4.5",
          text: "a realistic target that looks both strong and credible",
        },
      ],
    },
    how: {
      title: "How does the Google rating calculator work?",
      intro:
        "The calculator uses a simple weighted-average formula to find the exact number of 5-star reviews you need to reach your target rating. You only need three numbers:",
      steps: [
        {
          title: "Current number of reviews",
          text: "The review count shown on your Google Business Profile.",
        },
        {
          title: "Current average rating",
          text: "Your star rating out of 5, as shown by Google.",
        },
        {
          title: "Target rating",
          text: "The rating you want to reach (for example 4.5).",
        },
      ],
      outro:
        "The review calculator then works out how many new 5-star reviews you need, taking into account the weight of the reviews you already have. Use it to set realistic goals for your review strategy.",
    },
    examples: {
      title: "How many 5-star reviews do I need to raise my Google rating from 4.0 to 4.5?",
      intro:
        "It depends on how many reviews you already have. The more reviews you have, the more it takes to move your average.",
      items: [
        { header: "10 existing reviews", big: "~10" },
        { header: "50 existing reviews", big: "~50" },
        { header: "200 existing reviews", big: "~200" },
      ],
      itemText: "5-star reviews to go from 4.0 to 4.5",
      outro:
        "That is why it pays to start collecting reviews early: the fewer reviews you have, the bigger the impact each new 5-star review has on your overall rating.",
    },
    ideal: {
      title: "What Google rating should you aim for?",
      scale: {
        bad: "Too low",
        ok: "Fair",
        good: "Ideal",
        sus: "Too perfect?",
      },
      text: (
        <>
          A rating below 4.0 turns many potential customers away. On the other
          hand, a perfect 5.0 can look too good to be true. Your rating is also
          one of the signals Google looks at for the local pack. Aim for{" "}
          <strong>4.5 as a realistic goal</strong>.
        </>
      ),
    },
    tips: {
      title: "How to get more 5-star reviews on Google",
      intro:
        "Getting good reviews is not a matter of luck. Here is what successful local businesses do:",
      items: [
        {
          icon: "📩",
          title: "Ask every time",
          text: "After every visit or purchase, send a text or an email with a direct link to your Google review form. Ask all customers, not just the happy ones: Google does not allow selective review requests.",
        },
        {
          icon: "🔗",
          title: "Make it easy",
          text: "Use a short link or a QR code that opens the Google review form directly.",
        },
        {
          icon: "💬",
          title: "Reply to every review",
          text: "Positive and negative alike. Customers who see replies are more likely to leave a review of their own.",
        },
        {
          icon: "👥",
          title: "Train your team",
          text: "Everyone who talks to customers should know when and how to ask for a review.",
        },
        {
          icon: "⚡",
          title: "Automate review requests",
          text: "Tools like Localranker can send review requests automatically after each visit or purchase.",
        },
      ],
    },
    seoImpact: {
      title: "Do Google reviews affect local SEO?",
      intro:
        "Yes. Google's own guidance says that review count and rating factor into local search ranking, as part of a business's prominence. Three signals matter:",
      factors: [
        {
          icon: "📈",
          title: "Volume",
          text: "The more reviews you have, the more established and popular your listing looks.",
        },
        {
          icon: "⭐",
          title: "Average rating",
          text: "A high rating is a quality signal that helps your listing stand out.",
        },
        {
          icon: "🕐",
          title: "Recency",
          text: "Recent, steady reviews show that your business is active.",
        },
      ],
      outro:
        "By actively collecting reviews, you improve both your reputation and your visibility on Google Maps and in the local pack.",
    },
  },
};

export default content;
